import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../api';
import {
  PageHeader, Loading, ErrorBanner, Empty, Badge, StatCard, ui, fmtDate,
} from '../components/adminUi';
import OrderOffers from '../components/OrderOffers';
import Icon from '../components/Icon';
import { formatPhone } from '../lib/phone';
import { formatSom } from '../lib/money';
import { formatOrderDescription } from '../lib/orderDescription';
import { orderStatusLabel, orderStatusTone, ORDER_STATUS_FILTERS } from '../lib/orderStatus';

const POLL_MS = 15000;
const PAGE_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 400;

// Uzun matn: ko'pi bilan 2 qator (ellipsis), to'liq matn title atributida.
const clamp = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  wordBreak: 'break-word',
  color: 'var(--color-text)',
};

const fmtPrice = formatSom;

const RETRY_DELAY_MS = 600;
const LOAD_ERROR = "Ma'lumotlarni yuklashda xatolik yuz berdi.";
const isAbort = (e) => e && e.name === 'AbortError';
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Tarmoq xatosida (response yo'q, masalan ERR_FAILED / CORS) bitta avtomatik qayta urinish.
// Bekor qilingan (abort) so'rov qayta urinilmaydi.
async function fetchRetry(path, token, signal) {
  try {
    return await apiFetch(path, token, { signal });
  } catch (e) {
    if (isAbort(e) || signal.aborted) throw e;
    await sleep(RETRY_DELAY_MS);
    if (signal.aborted) throw e;
    return apiFetch(path, token, { signal });
  }
}

// Mijoz (buyurtma egasi) — ro'yxat qatori va mobil kartochka uchun.
const orderClient = (ord) => {
  const name = (ord.client && ord.client.name) || ord.clientName || ord.customerName || '';
  const phone = ord.client && ord.client.phone ? formatPhone(ord.client.phone) : '';
  return { name, phone };
};

// Takliflar xulosasi (mobil): soni + eng past narx.
function offersSummary(ord) {
  const offers = Array.isArray(ord.offers) ? ord.offers : [];
  const count = Number.isFinite(Number(ord.offersCount)) ? Number(ord.offersCount) : offers.length;
  if (!count) return "Hali takliflar yo'q";
  const prices = offers.map((f) => f.price).filter((v) => v != null && !Number.isNaN(Number(v))).map(Number);
  if (!prices.length) return `${count} ta taklif`;
  const min = Math.min(...prices);
  return `${count} ta taklif · ${count > 1 ? `${formatSom(min)} dan` : formatSom(min)}`;
}

export default function OrdersPage() {
  const { auth } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const loadSeq = useRef(0);
  const loadAbort = useRef(null);
  const [fetchError, setFetchError] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const timerRef = useRef(null);

  // Qidiruv: yozish tugagach (debounce) so'rov ketadi, sahifa 1 ga qaytadi.
  useEffect(() => {
    const next = searchInput.trim();
    const t = setTimeout(() => {
      setSearch(next);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Har yangi so'rov eskisini bekor qiladi (AbortController) va tartib raqami bilan himoyalanadi:
  // filtr tez almashtirilganda eski javob yangi natijani bosib ketmaydi.
  const load = useCallback(async (silent) => {
    if (loadAbort.current) loadAbort.current.abort();
    const ctrl = new AbortController();
    loadAbort.current = ctrl;
    const seq = ++loadSeq.current;
    const stale = () => seq !== loadSeq.current;
    if (!silent) setLoading(true);
    try {
      const q = new URLSearchParams({ page: String(page), limit: String(PAGE_LIMIT) });
      if (search) q.set('search', search);
      if (statusFilter) q.set('status', statusFilter);
      // Load both independently so one failing doesn't blank the whole page.
      const [statsRes, ordersRes] = await Promise.allSettled([
        fetchRetry('/api/admin/orders/stats', auth.token, ctrl.signal),
        fetchRetry(`/api/admin/orders?${q.toString()}`, auth.token, ctrl.signal),
      ]);
      if (stale()) return;

      let anyOk = false;
      if (statsRes.status === 'fulfilled' && statsRes.value.ok) {
        const st = await statsRes.value.json();
        if (stale()) return;
        setStats(st);
        anyOk = true;
      }
      if (ordersRes.status === 'fulfilled' && ordersRes.value.ok) {
        const data = await ordersRes.value.json();
        if (stale()) return;
        const list = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];
        setOrders(list);
        setTotal(Number(data.total) || list.length);
        anyOk = true;
      }
      setFetchError(anyOk ? '' : LOAD_ERROR);
      const now = new Date();
      setLastUpdated(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
    } catch (e) {
      if (isAbort(e) || stale()) return;
      setFetchError(LOAD_ERROR);
    } finally {
      if (!stale()) {
        setLoading(false);
        setLoaded(true);
      }
    }
  }, [auth.token, page, search, statusFilter]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  // Sahifadan chiqqanda oxirgi so'rov bekor qilinadi.
  useEffect(() => () => { if (loadAbort.current) loadAbort.current.abort(); }, []);

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
    // Qidiruv allaqachon bo'sh bo'lsa holat o'zgarmaydi — ro'yxatni qo'lda qayta yuklaymiz.
    if (!search) load();
  };

  // Yozilgan matn hali qidiruvga o'tmagan (debounce) bo'lsa ham yuklanish belgisi ko'rinadi.
  const busy = loading || searchInput.trim() !== search;

  const applyStatus = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  // Qator bosilsa — to'liq sahifa: /admin/orders/:id
  const openDetail = (id) => navigate(`/admin/orders/${id}`);

  const cards = [
    { label: 'Jami buyurtmalar', value: stats?.total, filter: '' },
    { label: "E'lon qilingan", value: stats?.published, filter: 'published', dot: 'var(--color-primary)' },
    { label: 'Faol', value: stats?.active, filter: 'active', dot: 'var(--color-dark)' },
    { label: 'Tasdiq kutilmoqda', value: stats?.awaitingConfirmation, filter: 'awaiting_confirmation', dot: 'var(--color-primary)' },
    { label: "E'tiroz bildirilgan", value: stats?.disputed, filter: 'disputed', dot: 'var(--color-danger)' },
    { label: 'Yakunlangan', value: stats?.completed, filter: 'completed', dot: 'var(--color-success)' },
    { label: 'Bekor qilingan', value: stats?.cancelled, filter: 'cancelled', dot: 'var(--color-danger)' },
  ];

  return (
    <AdminLayout>
      <PageHeader title="Buyurtmalar" subtitle="Real vaqtli statistika va buyurtmalar ro'yxati">
        <span style={ui.pagerInfo}>
          {lastUpdated ? `Yangilandi: ${lastUpdated}` : ''}
        </span>
        <button style={ui.pagerBtn} onClick={() => load()}><Icon name="refresh" size={14} style={{ marginRight: '6px' }} />Yangilash</button>
      </PageHeader>

      <ErrorBanner>
        {fetchError && (
          <span style={o.errorRow}>
            <span>{fetchError}</span>
            <button type="button" style={{ ...ui.btnSm, ...ui.btnRed }} onClick={() => load()} disabled={loading}>
              Qayta urinish
            </button>
          </span>
        )}
      </ErrorBanner>

      {!loaded && loading ? (
        <Loading />
      ) : (
        <>
          <div style={ui.statGrid}>
            {cards.map((c) => (
              <StatCard
                key={c.label}
                label={c.label}
                value={num(c.value)}
                dot={c.dot}
                selected={statusFilter === c.filter}
                onClick={() => applyStatus(c.filter)}
              />
            ))}
          </div>

          <div style={o.toolbar}>
            <div style={o.searchWrap}>
              <input
                type="text"
                style={{ ...ui.searchInput, ...o.searchInput }}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="ID, mijoz, telefon, tavsif yoki manzil bo'yicha qidirish"
                aria-label="Buyurtmani qidirish"
              />
              {searchInput && (
                <button type="button" style={o.clearBtn} onClick={clearSearch} aria-label="Tozalash" title="Tozalash">
                  <Icon name="x" size={16} />
                </button>
              )}
            </div>
          </div>
          <div style={o.chips} role="group" aria-label="Holat filtri">
            {ORDER_STATUS_FILTERS.map(([value, label]) => {
              const active = statusFilter === value;
              return (
                <button
                  key={value || 'all'}
                  type="button"
                  onClick={() => applyStatus(value)}
                  aria-pressed={active}
                  style={{ ...o.chip, ...(active ? o.chipActive : {}) }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {busy ? (
            <Loading />
          ) : orders.length === 0 ? (
            fetchError ? null : <Empty
              icon="package"
              title="Buyurtma topilmadi"
              hint={statusFilter || search ? "Qidiruv yoki filtrni o'zgartirib ko'ring." : "Yangi buyurtmalar bu yerda ko'rinadi."}
            />
          ) : (
            <>
              <div className="th-orders-cards" style={o.cards}>
                {orders.map((ord) => {
                  const client = orderClient(ord);
                  return (
                    <div
                      key={ord.id}
                      style={o.card}
                      role="button"
                      tabIndex={0}
                      onClick={() => openDetail(ord.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter') openDetail(ord.id); }}
                    >
                      <div style={o.cardTop}>
                        <span style={o.cardId}>#{ord.id}</span>
                        <Badge tone={orderStatusTone(ord.status)}>{orderStatusLabel(ord.status)}</Badge>
                      </div>
                      <div style={o.cardClient}>{client.name || '—'}{client.phone ? ` · ${client.phone}` : ''}</div>
                      <span style={clamp} title={formatOrderDescription(ord.description) || undefined}>{formatOrderDescription(ord.description) || '—'}</span>
                      <div style={o.cardMeta}>
                        <span style={o.cardOffers}>
                          {ord.budget != null ? `Byudjet: ${fmtPrice(ord.budget)}` : offersSummary(ord)}
                        </span>
                        {ord.budget != null && <span style={o.cardDate}>{offersSummary(ord)}</span>}
                        <span style={o.cardDate}>{fmtDate(ord.createdAt, true)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="th-orders-table" style={ui.tableWrap}>
                <table style={{ ...ui.table, minWidth: '900px' }}>
                  <thead>
                    <tr>
                      <th style={{ ...ui.th, width: '48px' }}>#</th>
                      <th style={{ ...ui.th, minWidth: '140px' }}>Tavsif</th>
                      <th style={{ ...ui.th, minWidth: '140px' }}>Manzil</th>
                      <th style={ui.th}>Holat</th>
                      <th style={ui.th}>Byudjet</th>
                      <th style={{ ...ui.th, minWidth: '200px' }}>Takliflar</th>
                      <th style={{ ...ui.th, whiteSpace: 'nowrap', minWidth: '140px' }}>Yaratilgan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((ord) => {
                      const offers = Array.isArray(ord.offers) ? ord.offers : [];
                      return (
                        <tr
                          key={ord.id}
                          style={{ ...ui.tr, cursor: 'pointer' }}
                          onClick={() => openDetail(ord.id)}
                          onKeyDown={(e) => { if (e.key === 'Enter') openDetail(ord.id); }}
                          tabIndex={0}
                        >
                          <td style={{ ...ui.td, color: 'var(--color-text-muted)' }}>{ord.id}</td>
                          <td style={{ ...ui.td, minWidth: '140px', maxWidth: '200px' }}>
                            <span style={clamp} title={formatOrderDescription(ord.description) || undefined}>
                              {formatOrderDescription(ord.description) || '—'}
                            </span>
                          </td>
                          <td style={{ ...ui.td, minWidth: '140px', maxWidth: '200px' }}>
                            <span style={clamp} title={ord.address || undefined}>{ord.address || '—'}</span>
                          </td>
                          <td style={{ ...ui.td }}>
                            <Badge tone={orderStatusTone(ord.status)}>{orderStatusLabel(ord.status)}</Badge>
                          </td>
                          <td style={{ ...ui.td, whiteSpace: 'nowrap' }}>{ord.budget != null ? fmtPrice(ord.budget) : '—'}</td>
                          <td style={{ ...ui.td, minWidth: '200px' }}>
                            <OrderOffers offers={offers} />
                          </td>
                          <td style={{ ...ui.td, whiteSpace: 'nowrap', minWidth: '140px' }}>{fmtDate(ord.createdAt, true)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div style={ui.pager}>
                <button
                  style={{ ...ui.pagerBtn, ...(page <= 1 ? ui.btnDisabled : {}) }}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  ← Oldingi
                </button>
                <span style={ui.pagerInfo}>{page} / {totalPages} · jami {total} ta</span>
                <button
                  style={{ ...ui.pagerBtn, ...(page >= totalPages ? ui.btnDisabled : {}) }}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Keyingi →
                </button>
              </div>
            </>
          )}
        </>
      )}

    </AdminLayout>
  );
}

const o = {
  toolbar: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' },
  searchWrap: { position: 'relative', flex: '1 1 260px', maxWidth: '480px', minWidth: 0 },
  searchInput: { width: '100%', minWidth: 0, paddingRight: '44px' },
  clearBtn: {
    position: 'absolute',
    top: '50%',
    right: '8px',
    transform: 'translateY(-50%)',
    width: '32px',
    height: '32px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    color: 'var(--color-text-muted)',
    border: 'none',
    borderRadius: 'var(--radius-btn)',
    cursor: 'pointer',
    padding: 0,
  },
  errorRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' },
  cards: { flexDirection: 'column', gap: '8px' },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '16px',
    background: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-card)',
    cursor: 'pointer',
    minWidth: 0,
  },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' },
  cardId: { fontSize: '14px', fontWeight: '700', color: 'var(--color-text)' },
  cardClient: { fontSize: '14px', fontWeight: '500', color: 'var(--color-text)', wordBreak: 'break-word' },
  cardMeta: { display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '2px' },
  cardOffers: { fontSize: '14px', fontWeight: '700', color: 'var(--color-text)' },
  cardDate: { fontSize: '12px', color: 'var(--color-text-muted)' },
  chips: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' },
  chip: {
    height: '36px',
    padding: '0 14px',
    whiteSpace: 'nowrap',
    background: 'var(--color-surface)',
    color: 'var(--color-text)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-chip)',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: '500',
  },
  chipActive: {
    background: 'var(--color-primary)',
    color: 'var(--color-on-primary)',
    borderColor: 'var(--color-primary)',
    fontWeight: '700',
  },
};

function num(v) {
  return v === null || v === undefined ? 0 : v;
}
